import { useQuery } from 'react-query'
import api from 'balkerne-core/api'
import type { HazardInfo } from '../types/climate'

export type HurricaneForecast = {
  name: string
  basin_id: string
  year: number
  gov_id: string
  is_active: boolean
  is_subtropical: boolean
  forecasts: {
    latitude: number
    longitude: number
    date: string
    sustained_wind: number
    max_gust_wind: number
    status: string
    max_radius: number
  }[]
  history: {
    latitude: number
    longitude: number
    date: string
    sustained_wind: number
    max_gust_wind: number
    status: string
    movement_degrees: number
    movement_speed: number
  }[]
}

export const useHurricaneForecasts = () => {
  const path = '/analytics/hurricanes/forecasts'
  const fetch = async () => await api.get(path).then(res => res.data)
  const { data, ...rest } = useQuery<HurricaneForecast[]>([path], fetch, {
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  })
  return { ...rest, data: data ?? [] }
}

export type HurricaneHistorical = {
  sid: string
  name: string
  last_timestamp: string
  year: number
}

export const useHurricanes = () => {
  const path = '/analytics/hurricanes'
  const fetch = async () => await api.get(path).then(res => res.data)
  const { data, ...rest } = useQuery<HurricaneHistorical[]>([path], fetch, {
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  })
  return { ...rest, data: data ?? [] }
}

export type HurricaneHistoricalTrack = {
  lat: number
  lon: number
  direction: number
  velocity: number
  track_type: string
  nature: string
  date: string
  agency: string
  interpolated: boolean
  roci: number | null
  wind_speed: number | null
  pressure: number | null
}

export const useHurricaneTrack = (sid: string | null) => {
  const path = `/analytics/hurricanes/${sid}`
  const fetch = async () => await api.get(path).then(res => res.data)
  const { data, ...rest } = useQuery<HurricaneHistoricalTrack[]>([path], fetch, {
    enabled: sid !== null,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  })
  return { ...rest, data: data ?? [] }
}

export const useThresholdRecommendation = (stationId, config = {}) => {
  const path = `/analytics/get_typical_high/${stationId}`
  const fetch = async () => await api.get(path).then(res => res.data)
  return useQuery([path], fetch, {
    ...config,
  })
}

export const sortClimateHazards = (hazards: HazardInfo[]): HazardInfo[] =>
  [...hazards].sort((left, right) => {
    if (left.order == null && right.order == null) {
      return left.id - right.id
    }
    if (left.order == null) {
      return 1
    }
    if (right.order == null) {
      return -1
    }
    if (left.order !== right.order) {
      return left.order - right.order
    }
    return left.id - right.id
  })

export const useClimateHazards = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const path = '/analytics/cc/hazards'
  const fetcher = async (): Promise<HazardInfo[]> => (await api.get(path)).data
  const { data, ...rest } = useQuery<HazardInfo[]>(path, fetcher, {
    enabled,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    select: sortClimateHazards,
  })
  return { ...rest, data: data ?? [] }
}

type FacetType = 'enum' | 'text' | 'bool'

export interface FacetDefinition {
  type: FacetType
  values?: string[]
}

export interface MetricGroup {
  id: number
  label: string
  order: number
}

export interface Metric {
  id: number
  hazard: string
  code: string
  label: string
  label_short?: string | null
  description?: string | null
  unit: string
  uses_return_periods?: boolean
  value_type: string
  facet_schema: Record<string, FacetDefinition>
  facet_defaults: Record<string, unknown>
  tags: Record<string, unknown>
  highlight: boolean
  hide: boolean
  order: number
  group: MetricGroup
}

export type MetricScenarioType = 'ssp' | 'rcp'

export type MetricReturnPeriodFilters = {
  values?: number[] | null
  min?: number | null
  max?: number | null
}

export type MetricDataFilterSet = {
  return_period?: MetricReturnPeriodFilters | null
  scenario_type?: MetricScenarioType[] | null
}

export type MetricDataFilters = MetricDataFilterSet & {
  country_overrides?: Record<string, MetricDataFilterSet> | null
}

export async function getClimateMetrics(hazard?: string): Promise<Metric[]> {
  const res = await api.get('/analytics/cc/metrics', {
    params: hazard ? { hazard } : undefined,
  })
  return res.data as Metric[]
}

export const useClimateMetrics = (hazard?: string) => {
  return useQuery({
    queryKey: ['climate-metrics', hazard ?? 'all'],
    queryFn: () => getClimateMetrics(hazard),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  })
}

export type MetricExportProfileMetricConfig = {
  metric_id: number
  use_defaults: boolean
  facet_filters?: Record<string, unknown> | null
  filters?: MetricDataFilters | null
}

export type MetricExportProfile = {
  id: number
  user_id: number
  name: string
  metrics: MetricExportProfileMetricConfig[]
  metric_schema_hashes: Record<string, string>
  hazard_filters?: Record<string, MetricDataFilters> | null
  created_at: string
  updated_at: string
}

export const useMetricExportProfiles = () => {
  const path = '/analytics/cc/metrics/profiles'
  const fetcher = async () => (await api.get(path)).data as MetricExportProfile[]

  return useQuery({
    queryKey: ['metric-export-profiles'],
    queryFn: fetcher,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  })
}

export type MetricExportJobStatus = 'QUEUED' | 'RUNNING' | 'COMPLETE' | 'FAILED'

export type MetricExportJobMetricKey = {
  hazard: string
  code: string
}

export type MetricExportJobSummary = {
  id: string
  status: MetricExportJobStatus
  created_at: string
  expires_at: string
  filename?: string | null
  file_size_bytes?: number | null
  error?: string | null
  property_ids: number[]
  metric_keys: MetricExportJobMetricKey[]
}

export const useMetricExportJobs = (opts?: { includeExpired?: boolean }) => {
  const includeExpired = Boolean(opts?.includeExpired)
  const path = '/analytics/cc/metrics/exports'
  const fetcher = async () =>
    (await api.get(path, { params: includeExpired ? { include_expired: true } : undefined }))
      .data as MetricExportJobSummary[]

  return useQuery({
    queryKey: ['metric-export-jobs', includeExpired],
    queryFn: fetcher,
    keepPreviousData: true,
    refetchOnWindowFocus: true,
    refetchInterval: data => {
      const jobs = (data as MetricExportJobSummary[] | undefined) ?? []
      const hasActive = jobs.some(j => j.status === 'QUEUED' || j.status === 'RUNNING')
      return hasActive ? 3000 : false
    },
  })
}
